import React from 'react';
import { useTranslation } from 'react-i18next';
import StyledContentBox from 'config/material-ui/styled/ContentBox.styled';
import { generateRangeDatesForBudgetCharts } from 'pages/BudgetPlanner/context/createBudgetConfigData';
import { Budget } from 'store/budget/interfaces';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import getBudgetsTransactionsByPeriod from './getBudgetsTransactionsByPeriod';
import BudgetTransactionRow from './BudgetTransactionRow';

export interface BudgetTransactionsListProps {
  budget: Budget;
}

export default function BudgetTransactionsList({
    budget,
}: BudgetTransactionsListProps) {
    const { t } = useTranslation();
    const displayedPeriod = generateRangeDatesForBudgetCharts(new Date(budget.dateFrom as string), new Date(budget.dateTo as string), budget);
    const displayedPeriodLabels = displayedPeriod.map(period => `${period.substring(0, 4)}-${period.substring(4)}`);
    const { groupedTransactions, groupedTransactionTypes } = getBudgetsTransactionsByPeriod(budget, displayedPeriod);
    const groupedTransactionsDataArray = Object.entries(groupedTransactions);

    return (
        <StyledContentBox>
            <Box px={3}>
                <TableContainer sx={{ mt: 3, maxHeight: 'calc(55vh - 12.8rem)' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={(theme) => ({
                                        minWidth: '25rem',
                                        left: 0,
                                        zIndex: 200,
                                        top: 0,
                                        position: 'sticky',
                                        borderTopLeftRadius: '1.2rem',
                                        borderBottomLeftRadius: '1.2rem',
                                        boxShadow: `-1.2rem 0 0 0 ${theme.palette.background.list}`,
                                    })}
                                >
                                    <Typography variant="tableHeader" color="text.secondary">
                                        {t('budget.transaction_name')}
                                    </Typography>
                                </TableCell>
                                {displayedPeriodLabels.map((period) => (
                                    <TableCell
                                        align="center"
                                        key={period}
                                        sx={{
                                            minWidth: '9.5rem',
                                            zIndex: 150,
                                            top: 0,
                                            position: 'sticky',
                                        }}
                                    >
                                        <Typography variant="tableHeader" color="text.secondary">
                                            {period}
                                        </Typography>
                                    </TableCell>
                                ))}
                                <TableCell
                                    colSpan={displayedPeriod.length}
                                    sx={{ zIndex: 150, top: 0, position: 'sticky' }}
                                />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groupedTransactionsDataArray.map(([transactionName, transaction]) => (
                                <BudgetTransactionRow
                                    key={transactionName}
                                    transaction={transaction}
                                    transactionName={transactionName}
                                    groupedTransactionTypes={groupedTransactionTypes}
                                    displayedPeriod={displayedPeriod}
                                />
                            ))}
                            {groupedTransactionsDataArray.length === 0 && (
                                <TableCell align="center" colSpan={displayedPeriodLabels.length + 1}>
                                    <Typography variant="bold">{t('label.no_data')}</Typography>
                                </TableCell>)}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </StyledContentBox>
    );
}
